/** @format */

import { EditorContent } from "@tiptap/react";
import { ImageIcon, PlusIcon, X } from "lucide-react";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { EditorToolbar } from "../../components/editor/EditorToolbar";
import { LinkBubbleMenu } from "../../components/editor/LinkBubbleMenu";
import SOPMediaSelector from "../../components/sop/SOPMediaSelector";
import { Button } from "../../components/ui/button";
import InputField from "../../components/ui/InputField";
import ProcessingTimer from "../../components/ui/ProcessingTimer";
import { useEditorConfig } from "../../hooks/useEditorConfig";
import { Step as StepType } from "../../types/sop";
import { replaceImageReferences } from "../../utils/imageReferences";
import { FilteredMedia } from "../../utils/mediaFiltering";
import {
  ensurePartIds,
  ensureToolIds,
  generatePartId,
  // generateToolId, // tools UI hidden — restore when re-enabling Tools section
} from "../../utils/stepIdentity";
import PartComponent from "./Part";
import "./StepEditor.css";
import { FeedbackBubbleMenu } from "./FeedbackBubbleMenu";
import { FieldFeedbackButton } from "./FieldFeedbackButton";
import {
  setFeedbackHighlights,
  getFeedbackIdsAtPos,
} from "./FeedbackHighlightExtension";
// import ToolComponent from "./Tool";
import { useDocumentPreviewStore } from "../../store/useDocumentPreviewStore";
import { useFeedbackStore } from "../../store/useFeedbackStore";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { useSuperAdmin } from "../../hooks/useSuperAdmin";
import { formatFeedbackCategoryLabel } from "../../api/feedbackService";

interface Props {
  index: number;
  step: StepType;
  sopId: string;
  sopTitle: string;
  versionId?: string;
  /** When true, step content is read-only (version locked). */
  readOnly?: boolean;
  /** When true, show a regeneration lock reason next to the step title. */
  regenerationLocked?: boolean;
  /** Optional custom message when `regenerationLocked` is true. */
  regenerationLockedMessage?: string;
  onFileUpload: (
    file: File,
    isLocal?: boolean,
  ) => Promise<{ imageRefId: string; downloadUrl: string }>;
  onStepChange: (updatedStep: StepType) => void;
  onDeleteStep?: () => void;
  canDelete?: boolean;
  canEdit?: boolean;
  /** When false, only the rich-text body is read-only; other controls (title, parts) remain editable. Defaults to canEdit. */
  canEditContent?: boolean;
  /**
   * When set, feedback submitted via the bubble menu will be attached to this
   * AI candidate rather than the published version step.
   */
  candidateId?: string;
  /** Passed through to FeedbackBubbleMenu — when false the bubble is hidden even for candidates. */
  feedbackEnabled?: boolean;
  stepsLength?: number;
  onStepTitleEdit?: (title: string) => void;
  getCurrentUrl: (imageRefId: string) => Promise<string | null>;
  getImageUrls: (
    refIds: string[],
  ) => Promise<Record<string, { url: string; expiresAt: number }>>;
  setImageUrls: (
    updater: (
      prev: Record<string, { url: string; expiresAt: number }>,
    ) => Record<string, { url: string; expiresAt: number }>,
  ) => void;
  imageUrls: Record<string, { url: string; expiresAt: number }>;
  media: FilteredMedia | null;
  mediaLoading: boolean;
  mediaError: string | null;
}

const StepEditor: FC<Props> = ({
  index,
  step,
  sopId,
  sopTitle,
  versionId,
  readOnly = false,
  regenerationLocked = false,
  regenerationLockedMessage,
  onFileUpload,
  onStepChange,
  onDeleteStep,
  canDelete = true,
  canEdit = false,
  canEditContent = canEdit,
  candidateId,
  feedbackEnabled = true,
  stepsLength = 1,
  onStepTitleEdit,
  getCurrentUrl,
  setImageUrls,
  imageUrls,
  media,
  mediaLoading,
  mediaError,
}) => {
  const normalizeStep = useCallback(
    (s: StepType): StepType => ({
      ...s,
      parts: ensurePartIds(s.parts, index),
      tools: ensureToolIds(s.tools, index),
    }),
    [index],
  );

  const [localStep, setLocalStep] = useState<StepType>(() =>
    normalizeStep(step),
  );
  const [processedContent, setProcessedContent] = useState<string>("");
  const [showMediaSelector, setShowMediaSelector] = useState(false);
  const [showHighlights, setShowHighlights] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState<{
    ids: string[];
    anchorRect: { top: number; right: number; bottom: number };
  } | null>(null);
  const isEditorFocused = useRef<boolean>(false);
  /** Only persist editor content after we've synced initial content from step; prevents empty editor from overwriting draft. */
  const initialContentSyncedRef = useRef(false);

  // Reset sync flag only when switching to a different step (index). Do not reset when step.details
  // changes (e.g. after we persist an edit), or we stop persisting further keystrokes.
  useEffect(() => {
    initialContentSyncedRef.current = false;
  }, [index]);

  // Process content with image URLs. In read-only mode always run so version content appears.
  //
  // Cancellation: if `step.details` changes while a previous `replaceImageReferences`
  // is still in flight (e.g. another edit fires before the URL fetch completes),
  // suppress the stale `setProcessedContent` so it can't overwrite the newer one.
  useEffect(() => {
    if (!readOnly && isEditorFocused.current) return;
    let cancelled = false;
    const processContent = async () => {
      try {
        const content = await replaceImageReferences(
          step.details || "",
          getCurrentUrl,
        );
        if (cancelled) return;
        setProcessedContent(content);
      } catch (error) {
        if (cancelled) return;
        console.error("Error processing image references:", error);
        setProcessedContent(step.details || "");
      }
    };
    processContent();
    return () => {
      cancelled = true;
    };
  }, [step.details, getCurrentUrl, readOnly]);

  // Sync localStep with step prop when it changes, but only if editor is not focused
  useEffect(() => {
    if (!isEditorFocused.current) {
      setLocalStep(normalizeStep(step));
    }
  }, [step, normalizeStep]);

  const editor = useEditorConfig(
    processedContent,
    useCallback(
      (content: string) => {
        if (readOnly) return;
        setLocalStep((prev) => {
          if (prev.details === content) return prev;

          // The original gate (`initialContentSyncedRef`) was added to prevent
          // the empty initial editor from overwriting a non-empty draft if
          // onUpdate fired before setContent had a chance to populate it. But
          // a hard ref-based gate also drops legitimate updates whenever the
          // editor fires onUpdate before the setTimeout in useEffect-234 has
          // flipped the ref to true — which can happen reliably under
          // StrictMode or when CustomImageNodeView's URL-resolve runs early.
          //
          // Replace it with a narrow guard: only block content that would
          // *wipe* a non-empty previous value to empty.
          const stripped = content.replace(/<[^>]+>/g, "").trim();
          const hasImage = /<img/i.test(content);
          if (!stripped && !hasImage) {
            const prevStripped = (prev.details || "").replace(/<[^>]+>/g, "").trim();
            const prevHasImage = /<img/i.test(prev.details || "");
            if (prevStripped || prevHasImage) {
              return prev;
            }
          }

          const updatedStep = { ...prev, details: content };
          onStepChange(updatedStep);
          return updatedStep;
        });
      },
      [onStepChange, readOnly],
    ),
    undefined,
    undefined,
    canEditContent,
  );

  // Track editor focus state
  useEffect(() => {
    if (editor) {
      const handleFocus = () => {
        isEditorFocused.current = true;
      };

      const handleBlur = () => {
        isEditorFocused.current = false;
      };

      editor.on("focus", handleFocus);
      editor.on("blur", handleBlur);

      return () => {
        editor.off("focus", handleFocus);
        editor.off("blur", handleBlur);
      };
    }
  }, [editor]);

  useEffect(() => {
    if (
      editor &&
      !editor.isDestroyed &&
      typeof (editor as { setEditable?: (v: boolean) => void }).setEditable ===
        "function"
    ) {
      (editor as { setEditable: (v: boolean) => void }).setEditable(!readOnly && canEditContent);
    }
  }, [editor, readOnly, canEditContent]);

  useEffect(() => {
    if (!editor || editor.isDestroyed || Object.keys(imageUrls).length === 0) {
      return;
    }
    if (!readOnly && isEditorFocused.current) return;
    let cancelled = false;
    const processContent = async () => {
      try {
        const content = await replaceImageReferences(
          step.details || "",
          getCurrentUrl,
        );
        if (cancelled) return;
        setProcessedContent(content);
      } catch (error) {
        if (cancelled) return;
        console.error("Error reprocessing image references:", error);
      }
    };
    processContent();
    return () => {
      cancelled = true;
    };
  }, [imageUrls, editor, step.details, getCurrentUrl, readOnly]);

  // Set editor content when processedContent changes. In read-only mode always sync;
  // in edit mode avoid overwriting while the user is focused.
  //
  // IMPORTANT: do NOT include `step.details` in the dep array. Including it
  // causes this effect to fire the moment `step.details` flips (e.g. right
  // after the user crops or annotates and `onStepChange` updates the store),
  // BEFORE `useEffect-139` has had a chance to recompute `processedContent`
  // asynchronously. At that point `processedContent` still holds the previous
  // value, so the comparison `processedContent !== editor.getHTML()` is true
  // and `setContent(processedContent)` reverts the editor to the pre-edit
  // content. step.details updates reach this effect indirectly: useEffect-139
  // observes the change, awaits replaceImageReferences, then calls
  // setProcessedContent — which IS in this dep array.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const shouldSync = readOnly || !isEditorFocused.current;
    if (!shouldSync) return;

    const rawDetails = (step.details || "").replace(/<[^>]+>/g, "").trim();
    const hasRawContent = rawDetails.length > 0;
    if (readOnly && !hasRawContent) {
      const emptyMessage = "<p>No details for this step.</p>";
      if (editor.getHTML() !== emptyMessage) {
        setTimeout(() => {
          if (editor && !editor.isDestroyed) {
            editor.commands.setContent(emptyMessage, { emitUpdate: false });
            initialContentSyncedRef.current = true;
          }
        }, 0);
      }
      return;
    }

    if (processedContent && processedContent !== editor.getHTML()) {
      setTimeout(() => {
        if (editor && !editor.isDestroyed) {
          editor.commands.setContent(processedContent, { emitUpdate: false });
          initialContentSyncedRef.current = true;
        }
      }, 0);
    } else if (!readOnly && !hasRawContent) {
      // Edit mode, step has no content; allow persistence so user can type.
      initialContentSyncedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, processedContent, readOnly]);

  useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, [editor]);

  // Close suggestion card when clicking outside (not on a highlight or the card)
  useEffect(() => {
    if (!selectedFeedback) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.closest?.(".feedback-highlight") ||
        target.closest?.("[data-feedback-card]")
      )
        return;
      setSelectedFeedback(null);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [selectedFeedback]);

  // Fetch feedback for this step when step is visible
  const fetchFeedbackForStep = useFeedbackStore((s) => s.fetchFeedbackForStep);
  useEffect(() => {
    if (!versionId || !sopId) return;
    fetchFeedbackForStep(sopId, versionId, index);
  }, [sopId, versionId, index, fetchFeedbackForStep]);

  // Apply feedback highlights from store (only when showHighlights is true; single selected in Suggestions tab or all when none selected)
  const feedbackByStep = useFeedbackStore((s) => s.feedbackByStep);
  const selectedFeedbackIdByStep = useFeedbackStore(
    (s) => s.selectedFeedbackIdByStep,
  );
  const setSelectedFeedbackIdForStep = useFeedbackStore(
    (s) => s.setSelectedFeedbackIdForStep,
  );
  const setOrphanedFeedbackIds = useFeedbackStore(
    (s) => s.setOrphanedFeedbackIds,
  );
  const updateFeedbackStatus = useFeedbackStore((s) => s.updateFeedbackStatus);
  const deleteFeedback = useFeedbackStore((s) => s.deleteFeedback);
  const { isSuperAdmin } = useSuperAdmin();

  const _stepKey = `${sopId}:${versionId}:${index}`;
  const _stepFeedback = feedbackByStep[_stepKey] ?? [];
  const _selectedFeedbackId = selectedFeedbackIdByStep[_stepKey] ?? null;
  // Only activate field ring for items submitted via FieldFeedbackButton (no editor position).
  // This prevents false rings if editor text accidentally matches a field label.
  const highlightedField =
    _stepFeedback.find(
      (f) => f.id === _selectedFeedbackId && f.selectionFrom == null,
    )?.selectedText ?? null;
  const [confirmDeleteFeedback, setConfirmDeleteFeedback] = useState<{
    sopId: string;
    feedbackId: string;
  } | null>(null);
  const [isDeletingFeedback, setIsDeletingFeedback] = useState(false);
  useEffect(() => {
    if (!editor || editor.isDestroyed || !versionId) return;
    const key = `${sopId}:${versionId}:${index}`;
    const items = feedbackByStep[key] ?? [];
    const selectedId = selectedFeedbackIdByStep[key];
    const toHighlight =
      selectedId != null ? items.filter((f) => f.id === selectedId) : items;

    const doc = editor.state.doc;

    const findTextInDoc = (
      needle: string,
    ): { from: number; to: number } | null => {
      if (!needle) return null;
      let found: { from: number; to: number } | null = null;
      doc.descendants((node, pos) => {
        if (found) return false;
        if (!node.isText || !node.text) return true;
        const idx = node.text.indexOf(needle);
        if (idx !== -1) {
          const from = pos + idx;
          found = { from, to: from + needle.length };
          return false;
        }
        return true;
      });
      return found;
    };

    // Orphaned = selected text no longer exists anywhere in the current step.
    // IMPORTANT: do NOT mark other feedback orphaned just because the original range drifted.
    // Field-label items (Step Title, Cycle Time, Part: X) have no selectionFrom/To by design —
    // they are never in the ProseMirror doc, so exclude them from orphan detection entirely.
    const orphanedIds: string[] = items
      .filter((f) => f.selectionFrom != null)
      .filter((f) => f.selectedText && f.selectedText.trim() !== "")
      .filter((f) => findTextInDoc(f.selectedText!) == null)
      .map((f) => f.id);

    setOrphanedFeedbackIds(key, orphanedIds);

    const resolveHighlightRange = (f: (typeof items)[number]) => {
      if (f.selectionFrom != null && f.selectionTo != null) {
        const from = f.selectionFrom;
        const to = f.selectionTo;
        if (from < to && to <= doc.content.size) {
          const sliceText = doc.textBetween(from, to, "\n", "\n");
          if (!f.selectedText || sliceText === f.selectedText)
            return { from, to };
        }
      }
      if (f.selectedText) return findTextInDoc(f.selectedText);
      return null;
    };

    const validHighlightCandidates = toHighlight
      .filter((f) => f.selectionFrom != null && f.selectionTo != null)
      .filter((f) => !orphanedIds.includes(f.id));

    const highlights =
      showHighlights && validHighlightCandidates.length > 0
        ? validHighlightCandidates
            .map((f) => {
              const range = resolveHighlightRange(f);
              if (!range) return null;
              return {
                from: range.from,
                to: range.to,
                feedbackId: f.id,
                selectedText: f.selectedText,
              };
            })
            .filter((x): x is NonNullable<typeof x> => x != null)
        : [];
    setFeedbackHighlights(
      editor,
      highlights,
      selectedFeedback?.ids[0] ?? selectedId ?? null,
    );
  }, [
    editor,
    feedbackByStep,
    selectedFeedbackIdByStep,
    sopId,
    versionId,
    index,
    showHighlights,
    selectedFeedback?.ids,
    setOrphanedFeedbackIds,
  ]);

  const handleFileSelect = async (file: File) => {
    try {
      const { imageRefId, downloadUrl } = await onFileUpload(file);
      console.log("Inserting uploaded file:", { imageRefId, downloadUrl });

      // Ensure editor is focused and cursor is positioned
      if (editor) {
        editor.commands.focus();

        // Use the custom image extension
        const success = editor.commands.setCustomImage({
          src: downloadUrl,
          width: 300,
          height: 200,
          "data-image-id": imageRefId,
        });

        console.log("Image insertion success:", success);
      }

      const updatedStep = {
        ...localStep,
        imageReferences: [...(localStep.imageReferences || []), imageRefId],
      };
      setLocalStep(updatedStep);
      onStepChange(updatedStep);
    } catch (error) {
      console.error(error);
    }
  };

  const handleRemovePart = (id: string) => {
    setLocalStep((prev) => {
      const updatedStep = {
        ...prev,
        parts: (prev.parts || []).filter((p) => (p.id ?? "") !== id),
      };
      onStepChange(updatedStep);
      return updatedStep;
    });
  };

  const handleAddPart = () => {
    setLocalStep((prev) => {
      const updatedStep = {
        ...prev,
        parts: [
          ...(prev.parts || []),
          { id: generatePartId(), name: "New Part", quantity: 1 },
        ],
      };
      onStepChange(updatedStep);
      return updatedStep;
    });
  };

  const handleEditPart = (id: string, name: string, quantity: number) => {
    setLocalStep((prev) => {
      const updatedStep = {
        ...prev,
        parts: (prev.parts || []).map((part) =>
          (part.id ?? "") === id ? { ...part, name, quantity } : part,
        ),
      };
      onStepChange(updatedStep);
      return updatedStep;
    });
  };

  // Tools section UI temporarily hidden — restore handlers + ToolComponent import when re-enabling.
  // const handleRemoveTool = (id: string) => {
  //   setLocalStep((prev) => {
  //     const updatedStep = {
  //       ...prev,
  //       tools: (prev.tools || []).filter((t) => (t.id ?? "") !== id),
  //     };
  //     onStepChange(updatedStep);
  //     return updatedStep;
  //   });
  // };

  // const handleAddTool = () => {
  //   setLocalStep((prev) => {
  //     const updatedStep = {
  //       ...prev,
  //       tools: [
  //         ...(prev.tools || []),
  //         { id: generateToolId(), name: "New Tool", cost: 0 },
  //       ],
  //     };
  //     onStepChange(updatedStep);
  //     return updatedStep;
  //   });
  // };

  // const handleEditTool = (
  //   id: string,
  //   name: string,
  //   cost: number,
  //   quantity: number,
  // ) => {
  //   setLocalStep((prev) => {
  //     const updatedStep = {
  //       ...prev,
  //       tools: (prev.tools || []).map((tool) =>
  //         (tool.id ?? "") === id ? { ...tool, name, cost, quantity } : tool,
  //       ),
  //     };
  //     onStepChange(updatedStep);
  //     return updatedStep;
  //   });
  // };

  const handleEditTitle = (title: string) => {
    const updatedStep = {
      ...localStep,
      title: title,
    };
    setLocalStep(updatedStep);
    onStepChange(updatedStep);
  };

  const handleMediaSelect = async (mediaUrl: string, mediaId: string) => {
    // Insert the selected media into the editor at the current cursor position
    if (editor) {
      console.log("Inserting media:", { mediaUrl, mediaId });
      const mediaUrlFromReference = await getCurrentUrl(mediaId);
      console.log("mediaUrlFromReferences", mediaUrlFromReference);

      // Ensure editor is focused and cursor is positioned
      editor.commands.focus();

      const success = editor.commands.setCustomImage({
        src: mediaUrl,
        width: 300,
        height: 200,
        "data-image-id": mediaId,
      });

      console.log("Media insertion success:", success);

      const newImageUrl = {
        url: mediaUrl,
        expiresAt: Date.now() + 60 * 60 * 1000,
      };
      setImageUrls((prev) => ({ ...prev, [mediaId]: newImageUrl }));

      const updatedStep = {
        ...localStep,
        details: editor.getHTML(),
        imageReferences: [...(localStep.imageReferences || []), mediaId],
      };
      setLocalStep(updatedStep);
      if (!readOnly) onStepChange(updatedStep);
    }
    setShowMediaSelector(false);
  };

  return (
    <div className="w-full flex items-start flex-wrap justify-between gap-24 relative">
      <div className="flex-1">
        {/* Step Title Section */}
        <div className="flex justify-between items-center mb-4 border-slate-700/30">
          <div className="flex flex-col gap-2 flex-1 min-w-0 mt-2">
            <div className="flex items-start justify-between gap-4 flex-1 min-w-0">
              <div className="flex flex-col flex-1">
                <div className="flex items-center gap-2" data-fb-target data-fb-id="step-title">
                  <h2 className="text-2xl font-bold flex items-center gap-2 overflow-visible flex-1 min-w-0">
                    <span>Step {index + 1}:</span>
                    <span
                      className={`ml-2 rounded transition-all relative inline-flex min-w-[4ch] ${
                        highlightedField === "Step Title"
                          ? "ring-2 ring-blue-400 ring-offset-1 ring-offset-slate-900"
                          : ""
                      }`}
                    >
                      {readOnly ? (
                        localStep.title || "Untitled step"
                      ) : (
                        <>
                          {/* phantom span so the wrapper auto-sizes to content width */}
                          <span className="invisible whitespace-pre text-2xl font-bold px-1 min-w-[4ch]">
                            {localStep.title || "Step title..."}
                          </span>
                          <InputField
                            value={localStep.title}
                            onSave={(title: string) =>
                              onStepTitleEdit
                                ? onStepTitleEdit(title)
                                : handleEditTitle(title)
                            }
                            className="absolute inset-0 text-2xl font-bold bg-transparent border-none outline-none overflow-visible w-full px-1"
                            placeholder="Step title..."
                            autoFocus={false}
                          />
                        </>
                      )}
                    </span>
                    {feedbackEnabled && versionId && (
                      <FieldFeedbackButton
                        sopId={sopId}
                        sopTitle={sopTitle}
                        versionId={versionId}
                        stepIndex={index}
                        candidateId={candidateId}
                        fieldLabel="Step Title"
                      />
                    )}
                  </h2>
                </div>
                {regenerationLocked && (
                  <span className="w-fit text-[12px] px-2 py-1 mt-2 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200">
                    {regenerationLockedMessage ?? "Update in progress"}
                  </span>
                )}
              </div>
              <div
                data-fb-target
                data-fb-id="cycle-time"
                className={`flex flex-col gap-2 rounded p-1 transition-all ${
                  highlightedField === "Cycle Time"
                    ? "ring-2 ring-blue-400 ring-offset-1 ring-offset-slate-900"
                    : ""
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <span
                    className="text-sm text-slate-300 font-normal"
                    title="Time to complete this step"
                  >
                    Cycle time
                  </span>
                  {feedbackEnabled && versionId && (
                    <FieldFeedbackButton
                      sopId={sopId}
                      sopTitle={sopTitle}
                      versionId={versionId}
                      stepIndex={index}
                      candidateId={candidateId}
                      fieldLabel="Cycle Time"
                    />
                  )}
                </div>
                <ProcessingTimer
                  processingTimeSeconds={
                    localStep.cycleTime != null
                      ? localStep.cycleTime * 60
                      : undefined
                  }
                  editable={!readOnly && canEdit}
                  onProcessingTimeChange={(seconds) => {
                    const updated = {
                      ...localStep,
                      cycleTime: seconds / 60,
                    };
                    setLocalStep(updated);
                    onStepChange(updated);
                  }}
                  className="ml-0"
                  label=""
                  tooltip="Step time (min and sec). Saved when you click Save SOP."
                  disabled={readOnly || !canEdit}
                />
              </div>
            </div>
          </div>
          
        </div>

        <div className="flex items-center justify-between mb-2">
          {!readOnly && canEditContent && (
            <Button
              className="flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 cursor-pointer bg-slate-600/10 border border-slate-600/20 text-slate-300 hover:bg-slate-600/20 hover:border-slate-600/30 hover:text-slate-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-slate-600/10"
              onClick={() => setShowMediaSelector(true)}
              title="Add media from SOP files"
            >
              <ImageIcon className="w-4 h-4" />
              Add Media
            </Button>
          )}

{onDeleteStep && canDelete && !readOnly && (
            <button
              className="flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 cursor-pointer bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 hover:border-red-500/30 hover:text-red-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-red-500/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:bg-red-500/10 disabled:hover:border-red-500/20 disabled:hover:text-red-400"
              onClick={onDeleteStep}
              disabled={stepsLength <= 1}
              title={
                stepsLength <= 1
                  ? "Cannot delete the last step"
                  : "Delete current step"
              }
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              <span>Delete</span>
            </button>
          )}
        </div>
        <div className="prose prose-invert max-w-none w-full h-full border rounded-lg border-gray-700 overflow-visible">
          {!readOnly && canEditContent && (
            <EditorToolbar
              editor={editor}
              onImageUpload={() => {
                const input = document.createElement("input");
                input.type = "file";
                input.accept = "image/*";
                input.onchange = async () => {
                  const fileList = input.files;
                  if (fileList && fileList.length) {
                    await handleFileSelect(Array.from(fileList)[0]);
                  }
                };
                input.click();
              }}
              suggestionsCount={
                versionId
                  ? (feedbackByStep[`${sopId}:${versionId}:${index}`] ?? [])
                      .length
                  : 0
              }
              showSuggestions={showHighlights}
              onToggleSuggestions={() => setShowHighlights((v) => !v)}
              onOpenSuggestionsPanel={() => {
                useDocumentPreviewStore
                  .getState()
                  .openSuggestionsPanel(`${sopId}:${versionId ?? ""}:${index}`);
                setShowHighlights(true);
              }}
            />
          )}
          <div
            className="min-h-[200px] p-4 overflow-visible"
            onClick={(e) => {
              const el = (e.target as HTMLElement).closest?.(
                ".feedback-highlight",
              );
              if (!(el instanceof HTMLElement) || !editor || !versionId) return;
              e.preventDefault();
              e.stopPropagation();

              // Map the click coordinates to a ProseMirror doc position so we
              // can find ALL feedback ranges that cover that exact offset —
              // not just the one whose <span> happens to be on top.
              const posResult = editor.view.posAtCoords({
                left: e.clientX,
                top: e.clientY,
              });
              const key = `${sopId}:${versionId}:${index}`;
              const items = feedbackByStep[key] ?? [];
              const highlights = items
                .filter((f) => f.selectionFrom != null && f.selectionTo != null)
                .map((f) => ({
                  from: f.selectionFrom!,
                  to: f.selectionTo!,
                  feedbackId: f.id,
                }));

              const ids =
                posResult != null
                  ? getFeedbackIdsAtPos(highlights, posResult.pos)
                  : el.dataset.feedbackId
                    ? [el.dataset.feedbackId]
                    : [];
              if (ids.length === 0) return;

              // Toggle: clicking the already-open highlight closes the popup.
              const alreadySelected =
                selectedFeedback !== null &&
                ids.length === selectedFeedback.ids.length &&
                ids.every((id) => selectedFeedback.ids.includes(id));

              if (alreadySelected) {
                setSelectedFeedback(null);
                setSelectedFeedbackIdForStep(key, null);
                return;
              }

              const rect = el.getBoundingClientRect();
              setSelectedFeedback({
                ids,
                anchorRect: {
                  top: rect.top,
                  right: rect.right,
                  bottom: rect.bottom,
                },
              });
              setSelectedFeedbackIdForStep(key, ids[0]);
            }}
          >
            {editor && (
              <>
                <EditorContent editor={editor} />
                <LinkBubbleMenu editor={editor} />
              </>
            )}
          </div>
        </div>

        <div className="section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="section-title">Parts</h3>
            {!readOnly && canEdit && (
              <Button
                className="btn btn-outline"
                onClick={() => handleAddPart()}
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Part
              </Button>
            )}
          </div>
          {localStep.parts && localStep.parts.length > 0 ? (
            <div className="flex flex-wrap gap-3">
              {localStep.parts.map((part) => (
                <div
                  key={part.id ?? `part-fb-${part.name}-${part.quantity}`}
                  data-fb-target
                  data-fb-id={`part-${part.name}`}
                  className={`rounded transition-all ${
                    highlightedField === `Part: ${part.name}`
                      ? "ring-2 ring-blue-400 ring-offset-1 ring-offset-slate-900"
                      : ""
                  }`}
                >
                  <PartComponent
                    name={part.name}
                    stock={part.quantity}
                    onEdit={(name: string, quantity: number) =>
                      handleEditPart(part.id!, name, quantity)
                    }
                    onDelete={() => handleRemovePart(part.id!)}
                    readOnly={readOnly}
                    feedbackButton={
                      feedbackEnabled && versionId ? (
                        <FieldFeedbackButton
                          sopId={sopId}
                          sopTitle={sopTitle}
                          versionId={versionId}
                          stepIndex={index}
                          candidateId={candidateId}
                          fieldLabel={`Part: ${part.name}`}
                        />
                      ) : undefined
                    }
                  />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No parts found.</p>
          )}
        </div>

        {/* Tools section temporarily hidden — redundant with step details; restore block above handlers if needed. */}
        {/* <div className="section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="section-title">Tools</h3>
            {!readOnly && canEdit && (
              <Button
                className="btn btn-outline"
                onClick={() => handleAddTool()}
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Tool
              </Button>
            )}
          </div>
          {localStep.tools && localStep.tools.length > 0 ? (
            <div className="flex flex-wrap gap-3">
              {localStep.tools.map((tool) => (
                <ToolComponent
                  key={tool.id ?? `tool-fb-${tool.name}-${tool.cost}`}
                  name={tool.name}
                  quantity={tool.quantity}
                  mpim={tool.mpim}
                  value={tool.value}
                  cost={tool.cost}
                  onEdit={(name, cost, quantity) =>
                    handleEditTool(tool.id!, name, cost, quantity)
                  }
                  onDelete={() => handleRemoveTool(tool.id!)}
                  readOnly={readOnly}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No tools found.</p>
          )}
        </div> */}
      </div>

      <FeedbackBubbleMenu
        editor={editor}
        sopId={sopId}
        sopTitle={sopTitle}
        versionId={versionId}
        stepIndex={index}
        candidateId={candidateId}
        feedbackEnabled={feedbackEnabled}
      />

      {selectedFeedback &&
        versionId &&
        (() => {
          const key = `${sopId}:${versionId}:${index}`;
          const items = feedbackByStep[key] ?? [];
          const fbs = selectedFeedback.ids
            .map((id) => items.find((f) => f.id === id))
            .filter(Boolean) as typeof items;
          if (fbs.length === 0) return null;

          const { anchorRect } = selectedFeedback;
          const cardLeft = Math.min(
            anchorRect.right + 12,
            window.innerWidth - 336,
          );

          const card = (
            <div
              data-feedback-card
              className={[
                // Keep below submit/loading overlays (e.g. feedback bubble menu).
                "fixed z-[9000] w-80 rounded-lg border",
                "border-slate-600 bg-slate-800 shadow-xl shadow-black/50",
              ].join(" ")}
              style={{ left: cardLeft, top: anchorRect.top }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-slate-700 px-3 py-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  {fbs.length === 1
                    ? formatFeedbackCategoryLabel(fbs[0])
                    : `${fbs.length} feedback`}
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedFeedback(null)}
                  className="rounded p-1 text-slate-500 hover:bg-slate-700 hover:text-slate-300"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <ul className="max-h-72 divide-y divide-slate-700/60 overflow-y-auto">
                {fbs.map((fb) => (
                  <li key={fb.id} className="space-y-1 px-3 py-2.5">
                    {fbs.length > 1 && (
                      <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                        {formatFeedbackCategoryLabel(fb)}
                      </span>
                    )}
                    <p className="text-sm text-slate-200">{fb.comment}</p>
                    {fb.selectedText && (
                      <p className="truncate text-xs italic text-slate-500">
                        &ldquo;{fb.selectedText}&rdquo;
                      </p>
                    )}
                    {(fb.createdByEmail ?? fb.createdBy) && (
                      <p className="text-xs text-slate-500" title="Author">
                        By {fb.createdByEmail ?? fb.createdBy}
                      </p>
                    )}
                    <div className="flex items-center justify-between pt-0.5">
                      <span
                        className={
                          fb.status === "resolved"
                            ? "text-xs text-emerald-400/80"
                            : "text-xs text-amber-400/80"
                        }
                      >
                        {fb.status}
                      </span>
                      <div className="flex items-center gap-1">
                        {fb.status === "open" && isSuperAdmin && (
                          <button
                            type="button"
                            onClick={() => {
                              updateFeedbackStatus(sopId, fb.id, "resolved");
                              setSelectedFeedback((prev) => {
                                if (!prev) return null;
                                const remaining = prev.ids.filter(
                                  (id) => id !== fb.id,
                                );
                                return remaining.length > 0
                                  ? { ...prev, ids: remaining }
                                  : null;
                              });
                            }}
                            className="rounded px-2 py-0.5 text-xs text-slate-400 hover:bg-slate-700 hover:text-emerald-400"
                          >
                            Resolve
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setConfirmDeleteFeedback({
                              sopId,
                              feedbackId: fb.id,
                            });
                          }}
                          className="rounded px-2 py-0.5 text-xs text-slate-400 hover:bg-slate-700 hover:text-red-400"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
          return createPortal(card, document.body);
        })()}

      <ConfirmDialog
        open={confirmDeleteFeedback != null}
        onClose={() => {
          if (isDeletingFeedback) return;
          setConfirmDeleteFeedback(null);
        }}
        onConfirm={async () => {
          if (!confirmDeleteFeedback) return;
          setIsDeletingFeedback(true);
          try {
            await deleteFeedback(
              confirmDeleteFeedback.sopId,
              confirmDeleteFeedback.feedbackId,
            );
            setSelectedFeedback((prev) => {
              if (!prev) return null;
              const remaining = prev.ids.filter(
                (id) => id !== confirmDeleteFeedback.feedbackId,
              );
              return remaining.length > 0 ? { ...prev, ids: remaining } : null;
            });
          } finally {
            setIsDeletingFeedback(false);
            setConfirmDeleteFeedback(null);
          }
        }}
        title="Delete feedback?"
        description="This will permanently delete this feedback."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={isDeletingFeedback}
      />

      {/* Media Selector Modal - Rendered via Portal */}
      {showMediaSelector &&
        createPortal(
          <SOPMediaSelector
            open={showMediaSelector}
            onClose={() => setShowMediaSelector(false)}
            media={media}
            loading={mediaLoading}
            error={mediaError}
            onMediaSelect={handleMediaSelect}
          />,
          document.body,
        )}
    </div>
  );
};

export default StepEditor;
